import React, { useState, useEffect, useMemo, useRef } from 'react';
import CapacityTrackerService from '@/services/CapacityTrackerService';
import type {
  AssignedPerson,
  DashboardHeader,
  ExternalCapacitySetting,
  Holiday,
  PersonInfo,
  PtoRequest,
  TaskTrackingItem,
} from '@/types/capacityTracker';
import type { Position } from '@/types/person';
import './CapacityTrackerStyles.css';
import type { Dashboard } from '@/types/dashboard';

// Type definitions
interface CapacityTrackerProps {
  config: {
    dashboardId: string;
    canEdit: boolean;
    dashboard: Dashboard;
    functions?: {
      openInfoWindow?: (item: any) => void;
    };
  };
  openConfig?: boolean;
  handleCloseModal?: () => void;
  canShowSaveOptions?: (show: boolean) => void;
  registerSaveFn: (fn: () => void) => void;
}

const CapacityTracker: React.FC<CapacityTrackerProps> = ({
  config,
  openConfig = false,
  handleCloseModal,
  canShowSaveOptions,
  registerSaveFn: _registerSaveFn,
}) =>
{
  const didRun = useRef(false);

  //   const personId = useMemo(() => internetStorageHelper.personId(), []);
  const today = useMemo(() =>
  {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  // State management
  const [itemsHolidays, setItemsHolidays] = useState<Holiday[] | null>(null);
  const [itemsPositions, setItemsPositions] = useState<Position[] | null>(null);
  const [_itemsProjects, _setItemsProjects] = useState<any[]>([]);
  const [selectProjectId, _setSelectProjectId] = useState<string | null>(null);
  const [selectDashboardId, _setSelectDashboardId] = useState<string>(config?.dashboardId);
  const [_itemsDashboards, setItemsDashboards] = useState<any[]>([]);
  const [capacityByPersonList, setCapacityByPersonList] = useState<PersonInfo[]>([]);
  const [dashboardHeader, setDashboardHeader] = useState<DashboardHeader | null>(null);
  const [_capacityWarning, setCapacityWarning] = useState<string | null>(null);
  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  const [canEdit] = useState(config?.canEdit);
  const [configurationIsOpen, setConfigurationIsOpen] = useState(false);
  const [ptoRequests, setPtoRequests] = useState<PtoRequest[]>([]);
  const [itemsAssigned, setItemsAssigned] = useState<AssignedPerson[] | null>(null);
  const [externalCapacitySettings, setExternalCapacitySettings] = useState<
    ExternalCapacitySetting[] | null
  >(null);
  const [currentWeekActiveDays, setCurrentWeekActiveDays] = useState<string[]>([
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
  ]);
  const [hideCompletedCards, setHideCompletedCards] = useState(false);
  const [taskTrackingItems, setTaskTrackingItems] = useState<TaskTrackingItem[]>([]);

  // Utility functions (unused but kept for reference)
  // const _strDateToMMDDYYYY = ...
  // const _parseDate = ...
  // const _calcDaysBetweenDates = ...

  const getPeriodDaysByActiveWeek = (activeWeek: string[] | null): number =>
  {
    // if (!dashboardHeader) return 0;

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const startDate =
      today < new Date(config.dashboard.StartDate) ? config.dashboard.StartDate : today;
    const dueDate = config.dashboard.DueDate;

    let days = 0;
    const currentCountingDate = new Date(startDate);
    currentCountingDate.setHours(0, 0, 0, 0);

    while (currentCountingDate <= new Date(dueDate))
    {
      const dayOfWeek = dayNames[currentCountingDate.getDay()] ?? '';

      if (activeWeek !== null)
      {
        if (activeWeek.includes(dayOfWeek))
        {
          days++;
        }
      }
      else
      {
        if (currentCountingDate.getDay() !== 0 && currentCountingDate.getDay() !== 6)
        {
          days++;
        }
      }
      currentCountingDate.setDate(currentCountingDate.getDate() + 1);
    }

    return days;
  };

  const getTeamByPersonId = (personId: string): string | null =>
  {
    const item = (itemsAssigned ?? []).find(x => Number(x.PersonID) === Number(personId));
    return item?.Team || null;
  };

  const getRoleByPersonId = (personId: string): string | null =>
  {
    const item = (itemsAssigned ?? []).find(x => Number(x.PersonID) === Number(personId));
    if (item)
    {
      const position = (itemsPositions ?? []).find(x => String(x.Id) === String(item.PositionId));
      if (position)
      {
        return position.Name;
      }
    }
    return null;
  };

  const getDaysOffByPersonId = (personId: string, activeWeek: string[]): number =>
  {
    // if (!dashboardHeader) return 0;

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const startDate =
      today < new Date(config.dashboard.StartDate) ? config.dashboard.StartDate : today;
    const dueDate = config.dashboard.DueDate;

    let days = 0;
    const personPtoRequestsDates = ptoRequests.filter(x => Number(x.PersonId) === Number(personId));
    const currentCountingDate = new Date(startDate);
    currentCountingDate.setHours(0, 0, 0, 0);

    while (currentCountingDate <= new Date(dueDate))
    {
      let isDayOff = false;
      let availableRequest: PtoRequest | undefined = undefined;

      const dayOfWeek = dayNames[currentCountingDate.getDay()] ?? '';

      if (activeWeek.includes(dayOfWeek))
      {
        isDayOff = (itemsHolidays ?? []).some(
          holiday => currentCountingDate.getTime() === new Date(holiday.Date).getTime(),
        );

        if (!isDayOff)
        {
          availableRequest = personPtoRequestsDates.find(
            period =>
              currentCountingDate >= new Date(period.DateFrom) &&
              currentCountingDate <= new Date(period.DateTo),
          );
          if (availableRequest)
          {
            isDayOff = true;
          }
        }
      }

      if (isDayOff)
      {
        if (availableRequest && availableRequest.IsHalfDay)
        {
          days += 0.5;
        }
        else
        {
          days++;
        }
      }

      currentCountingDate.setDate(currentCountingDate.getDate() + 1);
    }

    return days;
  };

  const getBarWidthPercent = (totalHours: number, totalBarSize: number): number =>
  {
    const width = (totalHours * 100) / totalBarSize;
    return Math.round(width);
  };

  // API calls
  const getHolidays = async () =>
  {
    const data = await CapacityTrackerService.getHolidays();
    setItemsHolidays(data as any);
  };

  const getPositions = async () =>
  {
    const data = await CapacityTrackerService.getPositions();
    setItemsPositions(data);
  };

  const getDashboardExternalCapacitySettings = async () =>
  {
    const data = await CapacityTrackerService.getDashboardCapacitySettings(
      selectDashboardId.toString(),
    );
    // Map PascalCase API → camelCase ExternalCapacitySetting
    const mapped = (Array.isArray(data) ? data : []).map((item: any) => ({
      personId: String(item.personId ?? item.PersonId ?? ''),
      dailyCapacity: item.dailyCapacity ?? item.DailyCapacity ?? 8,
      workingDays:
        item.workingDays ?? item.WorkingDays ?? 'monday,tuesday,wednesday,thursday,friday',
      hidden: item.hidden ?? item.Hidden ?? false,
    }));
    setExternalCapacitySettings(mapped);
  };

  const getAllAsignedTo = async () =>
  {
    const request = {
      Teams: '',
      GetTeamId: true,
      GetDepartmentId: false,
      GetPositionId: true,
      GetAllPeople: true,
    };
    const data = await CapacityTrackerService.getAllAsignedTo(request);
    setItemsAssigned(data as any);
  };

  // const _GetAllProjects = async () => { ... }; // unused

  const GetAllDashboards = async () =>
  {
    if (!selectProjectId)
    {
      setItemsDashboards([]);
      return;
    }
    const data = await CapacityTrackerService.getDashboardsByProjectIds(selectProjectId.toString());
    setItemsDashboards(data as any);
  };

  const getPeopleVacationsRequests = async (personIds: string[]): Promise<PtoRequest[]> =>
  {
    if (!dashboardHeader) return [];
    const params = {
      PersonIds: personIds.toString(),
      DateFrom:
        today < new Date(dashboardHeader.startDate)
          ? dashboardHeader.startDate
          : today.toLocaleDateString('en-US'),
      DateTo: dashboardHeader.dueDate.slice(0, 10),
    };

    const data = await CapacityTrackerService.getVacationRequestByIds(params);
    return data as any;
  };

  const setCurrentWeekActiveDaysFunc = () =>
  {
    let newCurrentWeekActiveDays = [...currentWeekActiveDays];

    const newToday = new Date(today);
    const dayOfWeek = newToday.getDay();
    const monday = new Date(newToday);
    monday.setDate(newToday.getDate() - ((dayOfWeek + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const currentCountingDate = new Date(monday);
    currentCountingDate.setHours(0, 0, 0, 0);

    while (currentCountingDate <= sunday)
    {
      const isDayOff = (itemsHolidays ?? []).some(
        holiday => currentCountingDate.getTime() === new Date(holiday.Date).getTime(),
      );

      if (isDayOff)
      {
        const day = currentCountingDate.getDay();
        const dayNames = [
          'sunday',
          'monday',
          'tuesday',
          'wednesday',
          'thursday',
          'friday',
          'saturday',
        ];
        const dayName = dayNames[day];
        newCurrentWeekActiveDays = newCurrentWeekActiveDays.filter(c => c !== dayName);
      }

      currentCountingDate.setDate(currentCountingDate.getDate() + 1);
    }

    setCurrentWeekActiveDays(newCurrentWeekActiveDays);
  };

  const orderCapacityItems = (taskItems: TaskTrackingItem[], peopleAssigned: AssignedPerson[]) =>
  {
    const users: { [key: string]: PersonInfo } = {};
    const defaultWeekWorkingDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

    const peopleWithTime: AssignedPerson[] = [];
    const peopleWithoutTime: AssignedPerson[] = [];

    const filteredTaskItems = hideCompletedCards
      ? taskItems.filter(x => !x.isCompleted)
      : taskItems;

    peopleAssigned.forEach(person =>
    {
      if (filteredTaskItems.some(item => Number(item.personId) === Number(person.PersonID)))
      {
        peopleWithTime.push(person);
      }
      else
      {
        peopleWithoutTime.push(person);
      }
    });

    peopleWithoutTime.forEach(person =>
    {
      const foundExternalUserSettings = (externalCapacitySettings ?? []).find(
        externalSettings => Number(externalSettings.personId) === Number(person.PersonID),
      );
      const dailyCapacity = foundExternalUserSettings ? foundExternalUserSettings.dailyCapacity : 8;
      const weekWorkingDays = foundExternalUserSettings
        ? foundExternalUserSettings.workingDays.split(',')
        : defaultWeekWorkingDays;

      const daysOff = getDaysOffByPersonId(person.PersonID, weekWorkingDays);
      const periodTotalDays =
        !config.dashboard?.StartDate || !config.dashboard?.DueDate
          ? // !dashboardHeader?.startDate || !dashboardHeader?.dueDate
          0
          : getPeriodDaysByActiveWeek(weekWorkingDays);

      const remainingWorkingDays = periodTotalDays - daysOff;
      const totalWorkingHours = periodTotalDays * dailyCapacity - daysOff * dailyCapacity;

      users[person.PersonID] = {
        personId: person.PersonID,
        assignedTo: person.Name,
        isExpanded: false,
        totalRemaining: 0,
        hasTime: false,
        role: getRoleByPersonId(person.PersonID),
        team: getTeamByPersonId(person.PersonID),
        totalWorkingHours,
        daysOff,
        remainingWorkingDays,
        weekWorkingDays,
        dailyCapacity,
        hidden: foundExternalUserSettings ? foundExternalUserSettings.hidden : false,
        itemsByLayer: [],
      };
    });

    filteredTaskItems.forEach(row =>
    {
      const { personId, layerId, layerName, assignedTo } = row;

      if (peopleWithTime.some(person => Number(person.PersonID) === Number(personId)))
      {
        if (!users[personId])
        {
          const foundExternalUserSettings = (externalCapacitySettings ?? []).find(
            externalSettings => Number(externalSettings.personId) === Number(personId),
          );
          const dailyCapacity = foundExternalUserSettings
            ? foundExternalUserSettings.dailyCapacity
            : 8;
          const weekWorkingDays = foundExternalUserSettings
            ? foundExternalUserSettings.workingDays.split(',')
            : defaultWeekWorkingDays;

          const remainingSum = filteredTaskItems.reduce((sum, item) =>
          {
            if (Number(item.personId) === Number(personId))
            {
              const currentRemaining = parseFloat(item.remaining.toString());
              if (!isNaN(currentRemaining))
              {
                return sum + currentRemaining;
              }
            }
            return sum;
          }, 0);

          const daysOff = getDaysOffByPersonId(personId, weekWorkingDays);
          const periodTotalDays =
            !config.dashboard.StartDate || !config.dashboard?.DueDate
              ? 0
              : getPeriodDaysByActiveWeek(weekWorkingDays);

          const remainingWorkingDays = periodTotalDays - daysOff;
          const totalWorkingHours = periodTotalDays * dailyCapacity - daysOff * dailyCapacity;

          const totalCards = filteredTaskItems.reduce((sum, item) =>
          {
            if (Number(item.personId) === Number(personId))
            {
              return sum + 1;
            }
            return sum;
          }, 0);

          users[personId] = {
            personId,
            assignedTo,
            isExpanded: false,
            totalRemaining: remainingSum,
            hasTime: true,
            role: getRoleByPersonId(personId),
            team: getTeamByPersonId(personId),
            totalWorkingHours,
            daysOff,
            remainingWorkingDays,
            weekWorkingDays,
            dailyCapacity,
            hidden: foundExternalUserSettings ? foundExternalUserSettings.hidden : false,
            itemsByLayer: [],
            totalCards,
          };
        }

        const currentUser = users[personId];
        let currentLayerItem = currentUser.itemsByLayer.find(
          itemLayer => itemLayer.layerId === layerId,
        );

        if (!currentLayerItem)
        {
          currentLayerItem = {
            layerId,
            layerName,
            capacityItems: [],
          };
          currentUser.itemsByLayer.push(currentLayerItem);
        }

        currentLayerItem.capacityItems.push({
          CoreTaskId: row.coreTaskId,
          CardName: row.cardName,
          AssignedTo: row.assignedTo,
          Estimated: row.estimated,
          Actual: row.actual,
          Remaining: row.remaining,
          ConversationId: row.conversationId,
          PhaseName: row.phaseName,
        });
      }
    });

    const usersArray = Object.values(users);
    usersArray.sort((a, b) => (b.hasTime ? 1 : 0) - (a.hasTime ? 1 : 0));

    if (capacityByPersonList.length > 0)
    {
      capacityByPersonList.forEach(oldPerson =>
      {
        const matchingPerson = usersArray.find(
          newPerson => Number(newPerson.personId) === Number(oldPerson.personId),
        );
        if (matchingPerson)
        {
          matchingPerson.isExpanded = oldPerson.isExpanded;
        }
      });
    }

    setCapacityByPersonList(usersArray);
    setIsLoadingRequests(false);
    canShowSaveOptions?.(usersArray.length > 0);
  };

  const getCapacityTrackerByDashboard = async () =>
  {
    const data = await CapacityTrackerService.getCapacityTrackerByDashboard(
      selectDashboardId.toString(),
    );
    console.log('CapacityTracker API response data:', data);

    // Sometimes the API wrapper lowers the first letter: dashboardHeader instead of DashboardHeader
    const raw = ((data as any).dashboardHeader ?? data.dashboardHeader ?? data) as any;
    console.log('Raw dashboard header object:', raw);

    // Map PascalCase API fields → camelCase local type
    const dashHeader: DashboardHeader = {
      name: raw.name ?? raw.Name ?? '',
      projectTitle: raw.projectTitle ?? raw.ProjectTitle ?? '',
      startDate: raw.startDate ?? raw.StartDate ?? '',
      dueDate: raw.dueDate ?? raw.DueDate ?? '',
      userIds: raw.userIds ?? raw.UserIds ?? '',
    };
    console.log('Mapped dashHeader:', dashHeader);

    const taskItems = (data.items ?? (data as any).items ?? []) as unknown as TaskTrackingItem[];

    dashHeader.totalHolidays = (itemsHolidays ?? []).filter(
      h =>
        new Date(h.Date) >= new Date(dashHeader.startDate) &&
        new Date(h.Date) <= new Date(dashHeader.dueDate),
    ).length;

    await setCurrentWeekActiveDaysFunc();
    setDashboardHeader(dashHeader);
    setTaskTrackingItems(taskItems);

    // If dueDate is null/empty, treat as no expiry (always valid)
    const isExpired = dashHeader.dueDate ? today > new Date(dashHeader.dueDate) : false;
    const hasValidDates = !!dashHeader.startDate && !!dashHeader.dueDate;

    if (!isExpired && hasValidDates)
    {
      const peopleAssigned = dashHeader.userIds ? dashHeader.userIds.split(',') : [];
      const validPeople = (itemsAssigned ?? []).filter(person =>
        peopleAssigned.some(assigned => assigned === String(person.PersonID)),
      );
      const peopleIds = validPeople.map(person => person.PersonID);

      const ptoData = await getPeopleVacationsRequests(peopleIds);
      const validPtoRequests = ptoData.filter(request => request.Status === 1 && request.IsActive);

      setPtoRequests(validPtoRequests);
      orderCapacityItems(taskItems, validPeople);
    }
    else
    {
      setCapacityByPersonList([]);
      setIsLoadingRequests(false);
      canShowSaveOptions?.(false);
    }
  };

  const generateTracker = () =>
  {
    if (selectDashboardId)
    {
      setIsLoadingRequests(true);
      setCapacityWarning(null);
      getCapacityTrackerByDashboard();
    }
    else
    {
      setCapacityWarning('Project and Dashboard fields are required.');
    }
  };

  const createLocalCapacitySettings = (usersArray: PersonInfo[]) =>
  {
    return usersArray.map(user => ({
      DashboardId: selectDashboardId,
      PersonId: user.personId,
      WorkingDays: user.weekWorkingDays.join(','),
      DailyCapacity: !user.dailyCapacity ? 0 : parseFloat(user.dailyCapacity.toString()),
      Hidden: user.hidden,
    }));
  };

  const updateDashboardExternalCapacitySettings = async (capacitySettings: any[]) =>
  {
    return CapacityTrackerService.updateDashboardCapacitySettings(capacitySettings);
  };

  // Event handlers
  const toggleHideCompletedCards = () =>
  {
    setHideCompletedCards(prev => !prev);
  };

  const toggleUserExpansion = (personId: string) =>
  {
    setCapacityByPersonList(prev =>
      prev.map(row =>
        Number(row.personId) === Number(personId) ? { ...row, isExpanded: !row.isExpanded } : row,
      ),
    );
  };

  const toggleHideUser = (personId: string) =>
  {
    setCapacityByPersonList(prev =>
      prev.map(user =>
        Number(user.personId) === Number(personId) ? { ...user, hidden: !user.hidden } : user,
      ),
    );
  };

  const toggleWorkingDayUser = (dayName: string, personId: string) =>
  {
    setCapacityByPersonList(prev =>
      prev.map(user =>
      {
        if (Number(user.personId) === Number(personId))
        {
          let newWeekWorkingDays = [...user.weekWorkingDays];

          if (newWeekWorkingDays.includes(dayName))
          {
            newWeekWorkingDays = newWeekWorkingDays.filter(name => name !== dayName);
          }
          else
          {
            newWeekWorkingDays.push(dayName);
          }

          return { ...user, weekWorkingDays: newWeekWorkingDays };
        }
        return user;
      }),
    );
  };

  const handleDailyCapacityChange = (e: React.ChangeEvent<HTMLInputElement>, personId: string) =>
  {
    const inputValue = e.target.value;
    const regex = /^\d*\.?\d{0,1}$/;

    const updatedValue =
      inputValue !== '' && regex.test(inputValue)
        ? parseFloat(inputValue) > 16
          ? 16
          : inputValue
        : '';

    setCapacityByPersonList(prev =>
      prev.map(user =>
        Number(user.personId) === Number(personId) ? { ...user, dailyCapacity: updatedValue } : user,
      ),
    );
  };

  const openInfoWindow = (item: any) =>
  {
    if (item.ConversationId && config.functions?.openInfoWindow)
    {
      config.functions.openInfoWindow(item);
    }
  };

  // const _onSelectProjectChange = ... // unused
  // const _onSelectDashboardChange = ... // unused

  const openConfiguration = () =>
  {
    setIsLoadingRequests(true);
    setTimeout(() =>
    {
      setConfigurationIsOpen(true);
      setIsLoadingRequests(openConfig);
    }, 0);
  };

  const closeConfiguration = () =>
  {
    setIsLoadingRequests(true);
    getDashboardExternalCapacitySettings();
    setTimeout(() =>
    {
      setConfigurationIsOpen(false);
    });
  };

  const saveConfiguration = () =>
  {
    const newCapacitySettings = createLocalCapacitySettings(capacityByPersonList);

    updateDashboardExternalCapacitySettings(newCapacitySettings).then(() =>
    {
      if (!openConfig)
      {
        closeConfiguration();
      }
      else
      {
        handleCloseModal?.();
      }
    });
  };

  // Effects
  //   useEffect(() => {
  //     loadConfig()
  //   }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Effects
  useEffect(() =>
  {
    if (didRun.current) return; // Prevent doble call of API en Restrict Mode
    didRun.current = true;
    loadConfig();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConfig = async () =>
  {
    await getPositions();

    if (openConfig)
    {
      setConfigurationIsOpen(true);
      canShowSaveOptions?.(false);
    }
  };

  useEffect(() =>
  {
    itemsPositions !== null && getHolidays();
  }, [itemsPositions]);

  useEffect(() =>
  {
    itemsHolidays !== null && getDashboardExternalCapacitySettings();
  }, [itemsHolidays]);

  useEffect(() =>
  {
    externalCapacitySettings !== null && getAllAsignedTo();
  }, [externalCapacitySettings]);

  useEffect(() =>
  {
    itemsAssigned !== null && generateTracker();
  }, [itemsAssigned]);

  useEffect(() =>
  {
    if (selectProjectId)
    {
      GetAllDashboards();
    }
  }, [selectProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() =>
  {
    if (hideCompletedCards !== undefined && dashboardHeader && taskTrackingItems.length > 0)
    {
      const peopleAssigned = dashboardHeader.userIds ? dashboardHeader.userIds.split(',') : [];
      const validPeople = (itemsAssigned ?? []).filter(person =>
        peopleAssigned.some(assigned => Number(assigned) === Number(person.PersonID)),
      );
      orderCapacityItems(taskTrackingItems, validPeople);
    }
  }, [hideCompletedCards]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() =>
  {
    if (capacityByPersonList.length > 0)
    {
      canShowSaveOptions?.(true);
    }
  }, [capacityByPersonList, canShowSaveOptions]);

  // Render
  const isDark = document.documentElement.classList.contains('dark');
  return (
    <div className={`${openConfig && 'flex flex-col  '}`} id="CapacityTracker">
      {!openConfig && (
        <div className="tracker-header">
          <div className="spaceRow">
            <div>
              <div className="stime-header">
                <h3>Capacity Tracker</h3>
                <p className="info_AR">
                  Monitor and manage team member availability for card/task distribution and
                  assignment.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="">
        {/* Loading Progress Bar */}
        <div
          className={`h-88 w-[1080px] max-w-[1080px] flex justify-center items-center ${isLoadingRequests ? 'visible' : 'hidden'}`}
        >
          <div>
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </div>

        {/* Main View Container */}
        {!configurationIsOpen && !isLoadingRequests && (
          <div
            className={
              capacityByPersonList.length > 0 ? 'tracker-results' : 'tracker-results-empty'
            }
          >
            {/* LEFT PANEL — Configuration (only shown when there is data) */}
            {capacityByPersonList.length > 0 && (
              <div style={{ flexShrink: 0, width: '45em' }}>
                <div
                  style={{
                    border: `1px solid ${isDark ? '#4a4c4c' : '#8080805c'}`,
                    padding: '24px',
                  }}
                >
                  <div className="configuration-header">
                    <div className="configuration-title">Configuration</div>

                    {canEdit && (
                      <div className="capacity-task-title edit-button" onClick={openConfiguration}>
                        <div>Edit</div>
                        <i className="fa fa-solid fa-edit"></i>
                      </div>
                    )}
                  </div>

                  <div className="configuration-week-info">
                    <div className="configuration-week-title">Team Working Days</div>
                    <div
                      style={{ border: `1px solid ${isDark ? '#4a4c4c' : '#8080805c'}` }}
                      className="configuration-week-container"
                    >
                      {[
                        'monday',
                        'tuesday',
                        'wednesday',
                        'thursday',
                        'friday',
                        'saturday',
                        'sunday',
                      ].map(dayName => (
                        <div
                          key={dayName}
                          className={
                            'configuration-week-day' +
                            (currentWeekActiveDays.includes(dayName) ? '' : ' inactive')
                          }
                        >
                          {dayName.charAt(0).toUpperCase()}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="configuration-week-title">Members</div>

                  {/* Members Container */}
                  <div className="configuration-members-container">
                    <div className="member-info">
                      <div>
                        {capacityByPersonList.map(
                          personInfo =>
                            !personInfo.hidden && (
                              <div key={personInfo.personId} style={{ marginBottom: '10px' }}>
                                <div
                                  style={{
                                    border: `1px solid ${isDark ? '#4a4c4c' : '#8080805c'}`,
                                  }}
                                  className="member-container flex-center-aligned"
                                >
                                  <span className="member-name">{personInfo.assignedTo}</span>
                                  <span className="member-daily-capacity">
                                    <div>Daily Capacity</div>
                                    {personInfo.dailyCapacity}h/day
                                  </span>
                                  <span className="member-end">
                                    <div>
                                      {personInfo.remainingWorkingDays} Remaining Working Days
                                    </div>
                                    <div>
                                      <span
                                        className="enable-help-tooltip"
                                        data-title="Calculated from today to dashboard end date"
                                      >
                                        {personInfo.daysOff} Days Off
                                      </span>
                                    </div>
                                  </span>
                                </div>
                              </div>
                            ),
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* RIGHT PANEL — Dashboard header + Members bars + Warning */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Main Dashboard Header Container */}
              {dashboardHeader && (
                <div>
                  <div
                    style={{
                      border: `1px solid ${isDark ? '#4a4c4c' : '#8080805c'}`,
                      padding: '24px',
                      width: '600px',
                      maxWidth: '630px',
                    }}
                  >
                    <div className="dashboard-header">
                      <div className="title">
                        {dashboardHeader.projectTitle} - {dashboardHeader.name}
                      </div>
                      <div className="period">
                        {dashboardHeader.startDate || dashboardHeader.dueDate
                          ? `${dashboardHeader.startDate ?? '-'} - ${dashboardHeader.dueDate ?? '-'}`
                          : '-'}
                      </div>
                      {capacityByPersonList.length > 0 && (
                        <div className="checkbox-hide-completed-capacity">
                          <span className="span-checkbox-hide-capacity">
                            <span
                              className="custom-checkbox task-card-checkbox"
                              style={{ margin: '4px' }}
                            >
                              <input
                                id="checkboxhidecapacity"
                                className="taskCardCheckbox"
                                type="checkbox"
                                checked={hideCompletedCards}
                                readOnly
                              />
                              <label
                                htmlFor="checkboxhidecapacity"
                                className="conversationCheckbox"
                                onClick={toggleHideCompletedCards}
                              ></label>
                            </span>
                            <span className="span-label-checkbox-hide-capacity">
                              Hide Closed Cards
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Main Members Container */}
              {capacityByPersonList.length > 0 && (
                <div>
                  <div
                    style={{
                      border: `1px solid ${isDark ? '#4a4c4c' : '#8080805c'}`,
                      marginTop: '11px',
                      padding: '24px',
                      width: '600px',
                      maxWidth: '630px',
                    }}
                  >
                    <div>
                      {capacityByPersonList.map(
                        personInfo =>
                          !personInfo.hidden && (
                            <div key={personInfo.personId} style={{ marginBottom: '26px' }}>
                              <div className="user-main-tracker flex-center-aligned">
                                <span className="flex-center-aligned">
                                  {personInfo.totalCards && personInfo.totalCards > 0 ? (
                                    <span className="arrow-container">
                                      <span
                                        id={`expandable-person-${personInfo.personId}`}
                                        className={`expandable-control expandable-control-menu ${
                                          personInfo.isExpanded ? 'expandable-control-expanded' : ''
                                        }`}
                                        onClick={() => toggleUserExpansion(personInfo.personId)}
                                      ></span>
                                    </span>
                                  ) : (
                                    <div style={{ width: '15px' }}></div>
                                  )}

                                  <div className="capacity-username">
                                    {personInfo.assignedTo}
                                    <div className="capacity-task-subtitle">
                                      ({personInfo.totalRemaining}h of{' '}
                                      {personInfo.totalRemaining >= 0
                                        ? personInfo.totalWorkingHours
                                        : 0}
                                      h)
                                    </div>
                                  </div>
                                </span>

                                {/* Progress bar */}
                                <span className="bar-with-percent flex-center-aligned">
                                  <div className="capacity-bar">
                                    <div
                                      style={{
                                        width:
                                          personInfo.totalWorkingHours <= 0
                                            ? '0%'
                                            : `${
                                              personInfo.totalRemaining >
                                                personInfo.totalWorkingHours
                                                ? 100
                                                : getBarWidthPercent(
                                                  personInfo.totalRemaining,
                                                  personInfo.totalWorkingHours,
                                                )
                                            }%`,
                                        height: '100%',
                                        backgroundColor:
                                          personInfo.totalRemaining > personInfo.totalWorkingHours
                                            ? 'red'
                                            : 'green',
                                      }}
                                    />

                                    <div
                                      className="line-limit"
                                      style={{
                                        marginLeft:
                                          (personInfo.totalWorkingHours <= 0
                                            ? 0
                                            : personInfo.totalRemaining >
                                                personInfo.totalWorkingHours
                                              ? getBarWidthPercent(
                                                personInfo.totalWorkingHours,
                                                personInfo.totalRemaining,
                                              )
                                              : 100) + '%',
                                      }}
                                    ></div>
                                  </div>
                                  <div className="percent-title">
                                    {personInfo.totalWorkingHours <= 0
                                      ? '0%'
                                      : `${getBarWidthPercent(
                                        personInfo.totalRemaining,
                                        personInfo.totalWorkingHours,
                                      )}%`}
                                  </div>
                                </span>
                              </div>

                              {/* Layers by user */}
                              {personInfo.isExpanded && (
                                <div style={{ paddingLeft: '20px' }}>
                                  {personInfo.itemsByLayer.map(layerInfo => (
                                    <div key={layerInfo.layerId}>
                                      <div className="capacity-layername flex-center-aligned">
                                        {layerInfo.layerName}
                                      </div>

                                      {/* Capacity items by layer */}
                                      {layerInfo.capacityItems.map(capacityItem => (
                                        <div
                                          key={capacityItem.CoreTaskId}
                                          className="task-main-tracker flex-center-aligned"
                                        >
                                          <div className="capacity-task">
                                            <div
                                              className="capacity-task-title"
                                              onClick={() => openInfoWindow(capacityItem)}
                                            >
                                              {capacityItem.CardName}
                                              {capacityItem.PhaseName
                                                ? ` - ${capacityItem.PhaseName}`
                                                : null}
                                            </div>
                                            <div className="capacity-task-subtitle">
                                              ({capacityItem.Remaining}h)
                                            </div>
                                          </div>

                                          <span className="bar-with-percent flex-center-aligned">
                                            <div className="capacity-bar">
                                              <div
                                                style={{
                                                  width:
                                                    personInfo.totalWorkingHours <= 0
                                                      ? '0%'
                                                      : `${getBarWidthPercent(
                                                        parseFloat(
                                                          capacityItem.Remaining.toString(),
                                                        ),
                                                        personInfo.totalWorkingHours,
                                                      )}%`,
                                                  height: '100%',
                                                  backgroundColor:
                                                    personInfo.totalRemaining >
                                                    capacityItem.Estimated
                                                      ? 'red'
                                                      : 'green',
                                                }}
                                              />
                                            </div>
                                            <div className="percent-title">
                                              {personInfo.totalWorkingHours <= 0
                                                ? '0%'
                                                : `${getBarWidthPercent(
                                                  parseFloat(capacityItem.Remaining.toString()),
                                                  personInfo.totalWorkingHours,
                                                )}%`}
                                            </div>
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ),
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Warning Information Container — shown when no data (equivalent to openedFromTab in legacy) */}
              {!openConfig && capacityByPersonList.length === 0 && dashboardHeader && (
                <div>
                  <div className="w-[600px] max-w-[630px] border mt-[11px] p-6 border-solid border-[rgba(128,128,128,0.36)] dark:border-[#4a4c4c]">
                    <div className="information-message">
                      <div className="instructions">
                        <div className="info-icon">
                          <i className="material-icons">info_outline</i>
                        </div>
                        <p>
                          You may not see the correct information if one of the following steps has
                          not been completed. Please ensure each step is followed.
                        </p>
                        <ul style={{ listStyleType: 'disc' }}>
                          <li>
                            <p>
                              <strong>Assign the Users:</strong> Go to the project conversation,
                              then find the user assignment field, enter the user's name, and
                              confirm the assignment.
                            </p>
                          </li>
                          <li>
                            <p>
                              <strong>Assign Remaining:</strong> Ensure the conversations have
                              values in the "remaining" field.
                            </p>
                          </li>
                          <li>
                            <p>
                              <strong>Assign Dates and Recalculate:</strong> Open the "Edit
                              Dashboard" modal in the dashboard tab, change the dates to a current
                              period, click "Recalculate" to update, verify all modifications, save
                              the changes.
                            </p>
                          </li>
                          <li>
                            <strong>Final Verification:</strong> Ensure the changes are correctly
                            reflected on the dashboard.
                          </li>
                        </ul>
                        <p>
                          Please follow these steps to ensure all modifications are correctly made
                          and the information is now visible.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Edit Configuration View Container */}
        {configurationIsOpen && !isLoadingRequests && (
          <div
            className={
              openConfig && capacityByPersonList.length > 0
                ? 'flex overflow-y-auto limit-scroll h-[70vh]'
                : 'flex w-full mb-8'
            }
          >
            <div className="w-full">
              {capacityByPersonList.length > 0 && (
                <div
                  className={`w-full max-w-[1040px] p-6 ${!openConfig && 'border border-[#8080805c] dark:border-[#4a4c4c] max-h-[40em] overflow-y-auto'}`}
                >
                  <div className="configuration-header">
                    <div className="configuration-title">Configuration</div>

                    {!openConfig && (
                      <div className="configuration-buttons flex-center-aligned">
                        <div
                          className="react-internet-wizard_cancel-btn"
                          onClick={closeConfiguration}
                        >
                          <div>Cancel</div>
                        </div>
                        <div
                          className="react-internet-wizard_internet-btn"
                          onClick={saveConfiguration}
                        >
                          <div>Save & Close</div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex-center-aligned">
                    {/* Default Working Days */}
                    <div className="configuration-week">
                      <div className="member-name">
                        <span className="enable-help-tooltip" data-title="Based on current week">
                          Default Working Days
                        </span>
                      </div>
                      <div
                        style={{ border: `1px solid ${isDark ? '#4a4c4c' : '#8080805c'}` }}
                        className="configuration-week-container"
                      >
                        {[
                          'monday',
                          'tuesday',
                          'wednesday',
                          'thursday',
                          'friday',
                          'saturday',
                          'sunday',
                        ].map(dayName => (
                          <div
                            key={dayName}
                            className={
                              'configuration-week-day' +
                              (currentWeekActiveDays.includes(dayName) ? '' : ' inactive')
                            }
                          >
                            {dayName.charAt(0).toUpperCase()}
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Team Holiday */}
                    <div className="member-end">
                      <div className="member-name">Team's Holidays</div>
                      <div className="member-end">{dashboardHeader?.totalHolidays}</div>
                    </div>
                    {/* Dashboard Dates */}
                    <div className="member-end">
                      <div className="member-name">Dates Period</div>
                      <div className="member-end">
                        {dashboardHeader?.startDate} - {dashboardHeader?.dueDate}
                      </div>
                    </div>
                  </div>

                  {/* Members Configuration Container */}
                  <div className="configuration-week-title">Members Configuration</div>
                  {capacityByPersonList.length > 0 && (
                    <div className="configuration-members-container">
                      <div className={`member-info${openConfig ? ' limit-scroll' : ''}`}>
                        <div>
                          {capacityByPersonList.map(personInfo => (
                            <div key={personInfo.personId} style={{ marginBottom: '10px' }}>
                              <div
                                style={{ border: `1px solid ${isDark ? '#4a4c4c' : '#8080805c'}` }}
                                className="member-configuration-container"
                              >
                                <div className="flex-center-aligned">
                                  <div className="member-name">{personInfo.assignedTo}</div>

                                  <div>
                                    <div
                                      className="hide-user-button"
                                      onClick={() => toggleHideUser(personInfo.personId)}
                                    >
                                      <i className="fa fa-solid fa-user"></i>
                                    </div>
                                  </div>
                                </div>

                                {!personInfo.hidden && (
                                  <div className="member-configuration-info gap-4">
                                    <span className="member-end">
                                      <div className="member-end">
                                        Team: {personInfo.team || '-'}
                                      </div>
                                      <div className="member-end">
                                        Role: {personInfo.role || '-'}
                                      </div>
                                    </span>

                                    <span className="member-week">
                                      {/* Configurable Working Days */}
                                      <div className="configuration-week">
                                        <div className="member-end">Working Days</div>
                                        <div
                                          style={{
                                            border: `1px solid ${isDark ? '#4a4c4c' : '#8080805c'}`,
                                          }}
                                          className="configuration-week-container"
                                        >
                                          {[
                                            'monday',
                                            'tuesday',
                                            'wednesday',
                                            'thursday',
                                            'friday',
                                            'saturday',
                                            'sunday',
                                          ].map(dayName => (
                                            <div
                                              key={dayName}
                                              className={
                                                'configurable configuration-week-day' +
                                                (personInfo.weekWorkingDays.includes(dayName)
                                                  ? ''
                                                  : ' inactive')
                                              }
                                              onClick={() =>
                                                toggleWorkingDayUser(dayName, personInfo.personId)
                                              }
                                            >
                                              {dayName.charAt(0).toUpperCase()}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </span>
                                    <span className="member-end">
                                      <div className="member-end">Days Off</div>
                                      <div className="member-end">{personInfo.daysOff}</div>
                                    </span>
                                    <span className="member-daily-capacity">
                                      <div className="member-end">Daily Capacity (h/day)</div>
                                      <div className="daily-capacity-input">
                                        <input
                                          type="text"
                                          name="dailycapacity"
                                          id={`dailycapacity${personInfo.personId}`}
                                          value={personInfo.dailyCapacity}
                                          inputMode="decimal"
                                          onChange={e =>
                                            handleDailyCapacityChange(e, personInfo.personId)
                                          }
                                        />
                                      </div>
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Warning Information Container */}
            {openConfig && capacityByPersonList.length === 0 && (
              <div>
                <div className="w-[600px] max-w-[630px] border mt-[11px] p-6 border-solid border-[rgba(128,128,128,0.36)] dark:border-[#4a4c4c]">
                  <div className="text-sm text-[#666] dark:text-[#a9a093] font-medium !leading-5 ">
                    <div className="flex flex-col gap-y-[10px]">
                      <div className="flex justify-center align-middle h-10 text-[40px] m-[10px]">
                        <i className="mi mi-info-outline" />
                      </div>

                      <p>
                        You may not see the correct information if one of the following steps has
                        not been completed. Please ensure each step is followed.
                      </p>
                      <ul className="flex flex-col gap-y-[10px] list-disc">
                        <li className="ml-10">
                          <p>
                            <strong className="text-[#056381] dark:text-[#62accb]">
                              Assign the Users:
                            </strong>{' '}
                            Go to the project conversation, then find the user assignment field,
                            enter the user's name, and confirm the assignment.
                          </p>
                        </li>
                        <li className="ml-10">
                          <p>
                            <strong className="text-[#056381] dark:text-[#62accb]">
                              Assign Remaining:
                            </strong>{' '}
                            Ensure the conversations have values in the "remaining" field.
                          </p>
                        </li>
                        <li className="ml-10">
                          <p>
                            <strong className="text-[#056381] dark:text-[#62accb]">
                              Assign Dates and Recalculate:
                            </strong>{' '}
                            Open the "Edit Dashboard" modal in the dashboard tab, change the dates
                            to a current period, click "Recalculate" to update, verify all
                            modifications, save the changes.
                          </p>
                        </li>
                        <li className="ml-10">
                          <strong className="text-[#056381] dark:text-[#62accb]">
                            Final Verification:
                          </strong>{' '}
                          Ensure the changes are correctly reflected on the dashboard.
                        </li>
                      </ul>
                      <p>
                        Please follow these steps to ensure all modifications are correctly made and
                        the information is now visible.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!openConfig ? <div className="separator"></div> : <div></div>}
          </div>
        )}
      </div>
      {openConfig && !isLoadingRequests && capacityByPersonList.length > 0 && (
        <div className="pt-3 text-right gap-[10px] relative min-h-6 flex flex-row-reverse">
          <button
            type="button"
            onClick={() => saveConfiguration()}
            className="bg-corgee-blue hover:bg-corgee-BlueHover dark:bg-[#3c6c80] dark:hover:brightness-[0.85] text-white text-xs h-7 px-3 py-1"
          >
            Save & Close
          </button>
          <button
            type="button"
            onClick={() => handleCloseModal?.()}
            className="bg-brightGray hover:bg-corgee-brightGrayHover dark:bg-[#5c6060] dark:hover:brightness-[0.85] text-white text-xs h-7 px-3 py-1"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default CapacityTracker;
